/**
 * Wheel Zoom Plugin for uPlot (X-axis only)
 * Based on: https://github.com/leeoniya/uPlot/blob/master/demos/zoom-wheel.html
 * Features: Mouse wheel zoom + Middle-click pan + Double-click reset
 */

import uPlot from "uplot";

function clamp(
  nRange: number,
  nMin: number,
  nMax: number,
  fRange: number,
  fMin: number,
  fMax: number
): [number, number] {
  if (nRange > fRange) {
    nMin = fMin;
    nMax = fMax;
  } else if (nMin < fMin) {
    nMin = fMin;
    nMax = fMin + nRange;
  } else if (nMax > fMax) {
    nMax = fMax;
    nMin = fMax - nRange;
  }
  return [nMin, nMax];
}

export function wheelZoomPlugin(opts: { factor?: number } = {}) {
  const factor = opts.factor || 0.75;

  // Store initial ranges for clamping and reset
  let xMin: number, xMax: number;
  let xRange: number;

  return {
    hooks: {
      ready: (u: uPlot) => {
        // Store initial data bounds for clamping
        xMin = u.scales.x.min!;
        xMax = u.scales.x.max!;
        xRange = xMax - xMin;

        const over = u.over;
        let rect = over.getBoundingClientRect();

        // Update rect on resize
        const updateRect = () => {
          rect = over.getBoundingClientRect();
        };

        // Double-click to reset zoom (X-axis only)
        over.addEventListener("dblclick", () => {
          u.setScale("x", { min: xMin, max: xMax });
        });

        // Middle mouse button drag for pan (X-axis only)
        over.addEventListener("mousedown", (e: MouseEvent) => {
          if (e.button === 1) {
            e.preventDefault();
            const left0 = e.clientX;
            const scXMin0 = u.scales.x.min!;
            const scXMax0 = u.scales.x.max!;
            const xUnitsPerPx = u.posToVal(1, "x") - u.posToVal(0, "x");

            function onmove(e: MouseEvent) {
              e.preventDefault();
              const dx = xUnitsPerPx * (e.clientX - left0);

              let newXMin = scXMin0 - dx;
              let newXMax = scXMax0 - dx;

              // Clamp to original bounds
              [newXMin, newXMax] = clamp(newXMax - newXMin, newXMin, newXMax, xRange, xMin, xMax);

              u.setScale("x", { min: newXMin, max: newXMax });
            }

            function onup() {
              document.removeEventListener("mousemove", onmove);
              document.removeEventListener("mouseup", onup);
            }

            document.addEventListener("mousemove", onmove);
            document.addEventListener("mouseup", onup);
          }
        });

        // Scroll wheel zoom centered on cursor (X-axis only)
        over.addEventListener("wheel", (e: WheelEvent) => {
          e.preventDefault();
          updateRect();

          const cursor = u.cursor;
          const left = cursor.left ?? rect.width / 2;

          const leftPct = left / rect.width;

          const xVal = u.posToVal(left, "x");

          const oxRange = u.scales.x.max! - u.scales.x.min!;

          // Zoom in on scroll up, zoom out on scroll down
          const nxRange = e.deltaY < 0 ? oxRange * factor : oxRange / factor;
          let nxMin = xVal - leftPct * nxRange;
          let nxMax = nxMin + nxRange;
          [nxMin, nxMax] = clamp(nxRange, nxMin, nxMax, xRange, xMin, xMax);

          u.setScale("x", { min: nxMin, max: nxMax });
        });
      },
    },
  };
}
